import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Toolbar from '@mui/material/Toolbar';
import MuiAppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import MailIcon from '@mui/icons-material/Mail';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AccountMenu from 'components/layouts/AccountMenu';

import app from 'app/config/app';
import { setIsDrawerOpen } from 'app/features/app/appSlice';
import { localStorageSave } from 'helpers/localStorage';
import constants from 'app/config/constants';
import SearchBox from 'core/forms/SearchBox';
import { getTransactions } from 'app/features/transactions/transactionSaga';
import { buildRoute } from 'routes';
import Notify from 'utils/Notify';
import isEmpty from 'helpers/isEmpty';

const drawerWidth = 280;
const drawerOpenWidth = 280;
const drawerClosedWidth = 80;

function Topbar({ drawerWidth, children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isDrawerOpen: open } = useSelector((state) => state.app);
  const { data: transactions, isLoading: isSearching } = useSelector(
    (state) => state.transactions.index,
  );

  const [searchValue, setSearchValue] = useState('');
  const [isSearchingTransaction, setIsSearchingTransaction] = useState(false);
  const [searchOrderId, setSearchOrderId] = useState(null);

  const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
  })(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer,
    boxShadow: 'none',
    background: 'inherit',

    [theme.breakpoints.down('md')]: {
      marginLeft: `calc(${theme.spacing(8)} + 1px)`,
      width: `calc(100% - ${theme.spacing(8)})`,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      width: `86%`,
    },
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
    ...(!open && {
      [theme.breakpoints.up('sm')]: {
        marginLeft: `calc(${theme.spacing(8)} + 1px)`,
        width: `calc(100% - ${theme.spacing(8)})`,
      },
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

  const handleDrawerToggle = () => {
    localStorageSave(constants.IS_DRAWER_OPEN, (!open).toString());
    dispatch(setIsDrawerOpen(!open));
  };

  const handleDrawerClose = () => {
    localStorageSave(constants.IS_DRAWER_OPEN, false.toString());
    dispatch(setIsDrawerOpen(false));
  };

  const isScreenSmaller = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const handleDrawerOnScreen = () => {
    if (isScreenSmaller && open) {
      handleDrawerClose();
    }
    handleDrawerToggle();
  };

  useEffect(() => {
    if (isScreenSmaller && open) {
      handleDrawerClose();
    }
  }, [isScreenSmaller, open]);

  useEffect(() => {
    if (isSearchingTransaction && !isSearching && searchOrderId) {
      const matchingTransactions = transactions.filter(
        (txn) => txn.id === searchOrderId || txn.order_id === searchOrderId,
      );

      setIsSearchingTransaction(false);

      if (isEmpty(matchingTransactions)) {
        Notify.error('No transaction found with the provided Order ID.');
        setSearchOrderId(null);
        return;
      }

      const transaction = matchingTransactions[0];
      navigate(buildRoute('transactions.show', transaction.id));
      setSearchValue('');
      setSearchOrderId(null);
    }
  }, [isSearching, transactions, isSearchingTransaction, searchOrderId, navigate]);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (!searchValue || !searchValue.trim()) {
      return;
    }

    const orderId = searchValue.trim();
    setSearchOrderId(orderId);
    setIsSearchingTransaction(true);

    dispatch(
      getTransactions({
        id: orderId,
        page_number: 1,
        page_size: 10,
      }),
    );
  };

  return (
    <AppBar position="fixed" open={open} elevation={0}>
      <Toolbar
        sx={{
          marginY: 2,
          marginLeft: 3,
          marginRight: 2,
          borderRadius: 8,
          boxShadow: '10px 8px 10px 2px rgba(0, 0, 0, 0.06)',
          zIndex: 999,
          backgroundColor: (theme) => (isScreenSmaller ? 'none' : theme.palette.primary.light),
        }}
      >
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOnScreen}
          edge="start"
          sx={{
            marginRight: '36px',
            ...(open && { display: 'none' }),
          }}
        >
          {!isScreenSmaller && (
            <MenuIcon
              sx={{
                color: 'black',
              }}
            />
          )}
        </IconButton>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          <Box>
            {open && (
              <MenuIcon
                sx={{ color: 'black', '&:hover': { cursor: 'pointer' } }}
                onClick={handleDrawerToggle}
              />
            )}
          </Box>

          <Box display="flex" gap="1" alignItems="center">
            <Box>
              <form onSubmit={handleSearchSubmit}>
                <SearchBox
                  placeholder="Transaction ID"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onClickClearSearch={handleClearSearch}
                  sx={{
                    width: 210,
                    height: 36,
                    '& input': {
                      padding: '6px 6px',
                      fontSize: '0.85rem',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.1rem',
                    },
                  }}
                />
              </form>
            </Box>

            <Box>
              {/* <IconButton
              size="large"
              aria-label="show 4 new mails"
              color="inherit"
              sx={{ 
                backgroundColor: '#105BB7',
                '&:hover': {
                  backgroundColor: '#105BB7',
                },
              }}
            >
              <Badge badgeContent={0} color="warning">
                <MailIcon sx={{ color: '#ffffff' }} />
              </Badge>
            </IconButton> */}
              <AccountMenu />
            </Box>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Topbar;

Topbar.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func,
  ]),
  open: PropTypes.bool,
  onDrawerOpen: PropTypes.func,
  drawerWidth: PropTypes.number,
};

Topbar.defaultProps = {
  children: null,
  open: false,
  onDrawerOpen: () => {},
  drawerWidth: app.drawerWith,
};
